import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {FieldArray, useFormikContext} from 'formik';

import {last6SchoolYears} from 'shared/utils';
import {getActivityCategories} from 'src/redux/lists';
import ExtracurricularService, {Extracurricular} from 'src/services/ExtracurricularService';
import {Store} from 'src/types/store';

import FormikRadioButtons from 'src/components/formik-radio-buttons';
import FormikCheckboxes from 'src/components/formik-checkboxes';
import FormikTypeahead from 'src/components/formik-typeahead';
import FormikDropdown from 'src/components/formik-dropdown';
import FormikTextArea from 'src/components/formik-text-area';
import TextButton from 'src/components/TextButton/TextButton';
import HighSchoolActivityLocation from 'shared/portfolio/components/activities/high-school-activity-location';
import TransferActivityLocation from 'shared/portfolio/components/activities/transfer-activity-location';
import LeadershipPositions from 'shared/portfolio/components/activities/leadership-positions';
import {ActivityForm} from 'shared/portfolio/components/activities/activities-form-wrapper';
import StudentReferrals from 'src/components/StudentReferrals';
import Label from 'shared/components/label';

import placeholders from 'src/static/placeholders.json';

const TYPES = [
  {value: 'activity', label: 'Activity/Club'},
  {value: 'sport', label: 'Organized Sport'},
];

const SCHOOL_YEARS = last6SchoolYears().map((year) => ({
  value: year,
  label: year,
}));

const PARTICIPATION = [
  {
    value: 'major',
    label: (
      <span>
        Major <span className="activity-form-subtext">4+ hrs/wk or 60+ hrs/yr</span>
      </span>
    ),
  },
  {
    value: 'medium',
    label: (
      <span>
        Medium <span className="activity-form-subtext">2-4 hrs/wk or 30-60 hrs/yr</span>
      </span>
    ),
  },
  {
    value: 'minor',
    label: (
      <span>
        Minor <span className="activity-form-subtext">&lt;2 hrs/wk or &lt;30 hrs/yr</span>
      </span>
    ),
  },
];

interface Props {
  referralTitle?: string;
}

const Activity = ({referralTitle = 'Invite your friends to join Raise Me (optional)'}: Props) => {
  const dispatch = useDispatch();
  const formik = useFormikContext<ActivityForm>();
  const activityCategories = useSelector((state: Store) => state.lists.activityCategories);
  const transfer = useSelector((state: Store) => state.transfer);
  const [isCustom, setIsCustom] = useState(false);
  const [activities, setActivities] = useState<(Extracurricular & {text: string})[]>([]);
  const [showDescription, setShowDescription] = useState(!!formik.values.description);

  useEffect(() => {
    dispatch(getActivityCategories());

    ExtracurricularService.index()
      .then((extracurriculars) =>
        extracurriculars.map((extracurricular) => ({
          ...extracurricular,
          text: extracurricular.name,
        }))
      )
      .then(setActivities);
  }, [dispatch]);

  return (
    <>
      <legend className="shared-required-legend"></legend>

      <Label required>
        Extracurricular Name
        <FormikTypeahead
          id="activity-name-typeahead"
          name="name"
          options={activities}
          allowNew
          onSelect={(value) => {
            setIsCustom(!!value.customOption);

            formik.setValues({
              ...formik.values,
              name: value.text.trim(),
              category: value.categories?.[0] || '',
              activityType: value.categories?.includes('Athletic') ? 'sport' : 'activity',
            });
          }}
          placeholder={placeholders.activity}
          required
        />
      </Label>

      <div>
        {isCustom && formik.values.activityType === 'activity' && (
          <Label required>
            Select Category
            <FormikDropdown
              name="category"
              options={activityCategories}
              placeholder="Select a category"
              required
            />
          </Label>
        )}
      </div>

      <FormikRadioButtons
        name="activityType"
        options={TYPES}
        legendText="Extracurricular Type"
        required
      />

      {transfer ? <TransferActivityLocation /> : <HighSchoolActivityLocation />}

      <FormikRadioButtons
        name="participation"
        options={PARTICIPATION}
        legendText="Time Commitment"
        required
      />

      <FormikCheckboxes
        name="schoolYears"
        label="Years (Select all that apply)"
        options={SCHOOL_YEARS}
        required
        className="mb-20"
      />

      {!transfer && <StudentReferrals title={referralTitle} />}

      {showDescription ? (
        <Label noTopPadding>
          Description (Optional)
          <FormikTextArea name="description" placeholder="Description..." />
        </Label>
      ) : (
        <TextButton
          type="button"
          color="blue"
          className="block mt-0 mb-20"
          onClick={() => setShowDescription(true)}
          buttonName="portfolio activities add-description-button add-a-description"
        >
          + Add a Description
        </TextButton>
      )}

      <FieldArray
        name="leadershipPositions"
        render={(arrayHelpers) => (
          <>
            {formik.values.leadershipPositions.length > 0 && (
              <Label>
                Leadership Positions
                <LeadershipPositions
                  _id={formik.values._id || 'new'}
                  handleRemove={(i) => arrayHelpers.remove(i)}
                />
              </Label>
            )}

            <TextButton
              type="button"
              color="blue"
              className="block mt-20"
              onClick={() => arrayHelpers.push({title: '', schoolYear: null, description: ''})}
              buttonName="portfolio activities leadership-positions add-leadership-position"
            >
              + Add Leadership Position
            </TextButton>
          </>
        )}
      />
    </>
  );
};

export default Activity;
